import { getNextjsApiBaseUrl } from '@/config/apiBaseUrl'
import { queryClient } from '@/lib/react-query'
import { useAuthStore } from '@/store/useAuthStore'
import {
  getCurrentUserQueryKey,
  type CurrentUserData,
  CURRENT_USER_CACHE_MS,
} from '@/hooks/queries/useCurrentUser'
import {
  teamSidebarContextKeys,
  TEAM_SIDEBAR_CONTEXT_CACHE_MS,
} from '@/hooks/queries/useTeamSidebarContext'
import { userTeamsKeys, type UserTeamSummary } from '@/hooks/useUserTeams'
import { useSessionUiStore } from '@/store/useSessionUiStore'
import { getFreelancerBillingProfileQueryOptions } from '@/hooks/queries/useFreelancerBillingProfile'

export interface AuthSessionTeamContext {
  team_id: string
  is_esn: boolean
  is_legacy_grandfathered: boolean
  disabled_sidebar_items: string[]
}

export interface AuthSessionPayload {
  user: CurrentUserData['user']
  role: string
  permissions: string[]
  team: {
    active_team_id: string | null
    active_team_role: string | null
    context: AuthSessionTeamContext | null
    freelancer_teams: UserTeamSummary[]
  }
  onboarding: {
    completed: boolean
    required_path: string | null
  }
  navigation: {
    default_path: string
  }
  impersonating?: boolean
  freelancer?: {
    show_billing_tab: boolean
  }
}

/** Réponse POST /auth/active-team — sans onboarding / navigation. */
export interface ActiveTeamSwitchPayload {
  team: AuthSessionPayload['team']
  user: Pick<
    AuthSessionPayload['user'],
    'team_tags' | 'team_tag' | 'is_esn'
  >
  freelancer?: AuthSessionPayload['freelancer']
}

interface AuthSessionResponse {
  code: number
  data?: AuthSessionPayload
}

/**
 * Résultat de GET /auth/session.
 *
 * `unauthenticated` et `unavailable` doivent rester distincts : le premier est une
 * réponse claire du serveur (la session n'est plus valide), le second veut dire
 * « on ne sait pas » (réseau coupé, API 5xx). Confondre les deux revient à
 * déconnecter l'utilisateur sur un simple blip réseau — typiquement au réveil
 * d'onglet, quand le navigateur reprend avant le réseau.
 */
export type AuthSessionOutcome =
  | { status: 'authenticated'; session: AuthSessionPayload }
  | { status: 'unauthenticated' }
  | { status: 'unavailable' }

const UNAUTHENTICATED: AuthSessionOutcome = { status: 'unauthenticated' }
const UNAVAILABLE: AuthSessionOutcome = { status: 'unavailable' }

/**
 * Interroge GET /auth/session sans jamais throw : un échec réseau est un état
 * métier attendu ici, pas une exception à faire remonter jusqu'au listener.
 */
export async function requestAuthSession(
  teamId?: string | null,
  options?: { allowTeamRetry?: boolean }
): Promise<AuthSessionOutcome> {
  const params = new URLSearchParams()
  if (teamId) {
    params.set('team_id', teamId)
  }
  const query = params.toString()
  const url = `${getNextjsApiBaseUrl()}/api/app/v1/auth/session${query ? `?${query}` : ''}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
  } catch {
    // TypeError: Failed to fetch — réseau indisponible, onglet réveillé trop tôt, DNS…
    return UNAVAILABLE
  }

  if (!response.ok) {
    // Stale persisted activeTeamId (ex. switch compte admin ↔ freelance) → retenter sans team_id
    if (
      response.status === 403 &&
      teamId &&
      options?.allowTeamRetry !== false
    ) {
      return requestAuthSession(null, { allowTeamRetry: false })
    }
    // 5xx : panne serveur, pas une session invalide.
    return response.status >= 500 ? UNAVAILABLE : UNAUTHENTICATED
  }

  let body: AuthSessionResponse
  try {
    body = (await response.json()) as AuthSessionResponse
  } catch {
    // 200 illisible (proxy, portail captif…) : indéterminé, on ne casse rien.
    return UNAVAILABLE
  }

  if (body.code !== 200 || !body.data?.user?.id) {
    return UNAUTHENTICATED
  }

  return { status: 'authenticated', session: body.data }
}

/**
 * Variante « payload ou rien » pour les appelants qui n'ont pas besoin de
 * distinguer session invalide et API injoignable (pages de login / callback).
 */
export async function fetchAuthSession(
  teamId?: string | null,
  options?: { allowTeamRetry?: boolean }
): Promise<AuthSessionPayload | null> {
  const outcome = await requestAuthSession(teamId, options)
  return outcome.status === 'authenticated' ? outcome.session : null
}

/** Applique (ou réinitialise) les flags UI équipe dans le store sessionUi. */
function applyTeamContextToUiStore(context: AuthSessionTeamContext | null): void {
  if (context) {
    useSessionUiStore.getState().setTeamUi({
      is_esn: context.is_esn,
      is_legacy_grandfathered: context.is_legacy_grandfathered,
      disabled_sidebar_items: context.disabled_sidebar_items,
    })
  } else {
    useSessionUiStore.getState().clear()
  }
}

/** Applique les flags UI freelance depuis la session auth. */
function applyFreelancerSettingsFromSession(
  role: string,
  freelancer?: AuthSessionPayload['freelancer'],
): void {
  if (role !== 'freelancer') {
    useSessionUiStore.setState({
      showFreelancerBillingTab: false,
      freelancerUiReady: false,
    })
    return
  }

  useSessionUiStore.getState().setFreelancerUi({
    show_billing_tab: freelancer?.show_billing_tab === true,
  })
}

function seedReactQueryFromSession(session: AuthSessionPayload): void {
  const userId = session.user.id
  const teamId = session.team.active_team_id

  const currentUserData: CurrentUserData = {
    user: session.user,
    role: session.role,
    permissions: session.permissions,
  }

  queryClient.setQueryData(getCurrentUserQueryKey(userId, teamId ?? null), currentUserData)
  queryClient.setQueryDefaults(getCurrentUserQueryKey(userId, teamId ?? null), {
    staleTime: CURRENT_USER_CACHE_MS,
    refetchOnMount: false,
  })

  if (session.team.context) {
    queryClient.setQueryData(
      teamSidebarContextKeys.detail(session.team.context.team_id),
      session.team.context,
      { updatedAt: Date.now() }
    )
    queryClient.setQueryDefaults(teamSidebarContextKeys.detail(session.team.context.team_id), {
      staleTime: TEAM_SIDEBAR_CONTEXT_CACHE_MS,
      gcTime: TEAM_SIDEBAR_CONTEXT_CACHE_MS,
    })
  }

  if (session.team.active_team_id && session.team.active_team_role) {
    queryClient.setQueryData(
      ['teamRole', userId, session.team.active_team_id],
      session.team.active_team_role
    )
  }

  if (session.team.freelancer_teams?.length) {
    queryClient.setQueryData(userTeamsKeys.list(userId), session.team.freelancer_teams)
    queryClient.setQueryDefaults(userTeamsKeys.list(userId), {
      staleTime: 1000 * 60 * 10,
      refetchOnMount: false,
    })
  }

  if (
    session.role === 'freelancer' &&
    session.freelancer?.show_billing_tab &&
    teamId
  ) {
    void queryClient.prefetchQuery(getFreelancerBillingProfileQueryOptions(teamId))
  }
}

/** Hydrate Zustand + React Query depuis GET /auth/session. */
export function applyAuthSessionToClient(session: AuthSessionPayload): void {
  const { setAuth } = useAuthStore.getState()
  const persistedTeamId = useAuthStore.getState().activeTeamId
  const isFreelancer = session.role === 'freelancer'

  let resolvedTeamId = session.team.active_team_id
  if (isFreelancer && persistedTeamId) {
    // Persist freelancer prioritaire si l'équipe est toujours valide ; sinon défaut serveur.
    const persistedValid = session.team.freelancer_teams?.some((t) => t.id === persistedTeamId)
    if (persistedValid) {
      resolvedTeamId = persistedTeamId
    }
  }

  setAuth({
    user: session.user,
    role: session.role,
    permissions: session.permissions,
  })

  applyTeamContextToUiStore(session.team.context)
  applyFreelancerSettingsFromSession(session.role, session.freelancer)

  useAuthStore.setState({
    activeTeamId: resolvedTeamId,
    activeTeamRole: session.team.active_team_role,
    sessionRequiredPath: session.onboarding.required_path,
    sessionDefaultPath: session.navigation.default_path,
    sessionHydrated: true,
    impersonating: Boolean(session.impersonating),
  })

  seedReactQueryFromSession({
    ...session,
    team: {
      ...session.team,
      active_team_id: resolvedTeamId,
    },
  })

  // Prefetch liste de la page courante en parallèle du shell (sidebar + chunks lazy).
  void import('@/lib/navigation/prefetchSidebarRoute').then(({ prefetchInitialRouteAfterSessionHydrate }) => {
    prefetchInitialRouteAfterSessionHydrate({
      role: session.role,
      activeTeamId: resolvedTeamId,
    })
  })
}

export async function fetchActiveTeamSwitch(
  teamId: string
): Promise<ActiveTeamSwitchPayload | null> {
  const url = `${getNextjsApiBaseUrl()}/api/app/v1/auth/active-team`

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ team_id: teamId }),
  })

  if (!response.ok) {
    return null
  }

  const body = (await response.json()) as {
    code: number
    data?: { session?: ActiveTeamSwitchPayload }
  }

  if (body.code !== 200 || !body.data?.session) {
    return null
  }

  return body.data.session
}

/** Switch équipe léger — met à jour team context + tags sans GET /auth/session complet. */
export function applyActiveTeamSwitchToClient(
  switchPayload: ActiveTeamSwitchPayload
): void {
  const { user, globalRole, permissions } = useAuthStore.getState()
  if (!user?.id) return

  const resolvedTeamId = switchPayload.team.active_team_id
  const mergedUser = {
    ...user,
    team_tags: switchPayload.user.team_tags,
    team_tag: switchPayload.user.team_tag,
    is_esn: switchPayload.user.is_esn,
  }

  useAuthStore.getState().setAuth({
    user: mergedUser,
    role: globalRole,
    permissions: permissions ?? [],
  })

  applyTeamContextToUiStore(switchPayload.team.context)
  applyFreelancerSettingsFromSession(globalRole ?? user.role, switchPayload.freelancer)

  useAuthStore.setState({
    activeTeamId: resolvedTeamId,
    activeTeamRole: switchPayload.team.active_team_role,
    sessionHydrated: true,
  })

  const { sessionRequiredPath, sessionDefaultPath } = useAuthStore.getState()

  const sessionSlice: AuthSessionPayload = {
    user: mergedUser,
    role: globalRole ?? user.role,
    permissions: permissions ?? [],
    team: {
      ...switchPayload.team,
      active_team_id: resolvedTeamId,
    },
    onboarding: {
      completed: !sessionRequiredPath,
      required_path: sessionRequiredPath,
    },
    navigation: {
      default_path: sessionDefaultPath ?? '/missions',
    },
    freelancer: switchPayload.freelancer,
  }

  seedReactQueryFromSession(sessionSlice)

  void import('@/lib/navigation/prefetchSidebarRoute').then(
    ({ resetSidebarPrefetchCache, prefetchInitialRouteAfterSessionHydrate }) => {
      resetSidebarPrefetchCache()
      prefetchInitialRouteAfterSessionHydrate({
        role: globalRole ?? user.role,
        activeTeamId: resolvedTeamId,
      })
    }
  )
}

/** Fetch + hydrate en une passe (cookies HttpOnly uniquement). */
let hydrateAuthSessionInFlight: Promise<AuthSessionOutcome> | null = null

/**
 * Fetch + hydrate en conservant le détail de l'échec. À utiliser partout où un
 * échec déclenche une action destructive (purge du store, redirection login).
 */
export async function hydrateAuthSession(): Promise<AuthSessionOutcome> {
  if (hydrateAuthSessionInFlight) {
    return hydrateAuthSessionInFlight
  }

  hydrateAuthSessionInFlight = (async () => {
    const { activeTeamId } = useAuthStore.getState()
    const outcome = await requestAuthSession(activeTeamId)
    if (outcome.status === 'authenticated') {
      applyAuthSessionToClient(outcome.session)
    }
    return outcome
  })().finally(() => {
    hydrateAuthSessionInFlight = null
  })

  return hydrateAuthSessionInFlight
}

/** Variante booléenne pour les appelants sans branche destructive. */
export async function fetchAndHydrateAuthSession(): Promise<boolean> {
  const outcome = await hydrateAuthSession()
  return outcome.status === 'authenticated'
}
